import React from 'react';

import styled from '@emotion/styled';

const PrivacySettings = () => {
  return (
    <Container>
      <h2>Locumspace Privacy Policy</h2>
      <p>
        <strong>Effective Date:</strong> 13/09/2024
      </p>

      <h2>Welcome to Locumspace!</h2>
      <p>
        This Privacy Policy outlines how Locumspace ("we," "us," or "our")
        collects, uses, discloses, and protects your personal information when
        you use our website located at{' '}
        <a href='http://locumspace.co' target='_blank' rel='noreferrer'>
          locumspace.co
        </a>{' '}
        (the "Website") and our mobile application called Locumspace (the
        "App"). This Privacy Policy is compliant with global standards and the
        National Data Protection Act of Nigeria 2023 ("NDPA 2023").
      </p>
      <p>
        By accessing or using our Website or App, you agree to the terms of this
        Privacy Policy. If you do not agree with these terms, please do not
        access or use our services.
      </p>

      <h2>Information We Collect</h2>
      <ul>
        <li>
          <strong>Information You Provide:</strong> When you register an
          account, use our services, or communicate with us, we may collect
          personal information such as your name, email address, phone number,
          and professional qualifications.
        </li>
        <li>
          <strong>Automatically Collected Information:</strong> We may collect
          information about your device and usage patterns, including IP
          address, browser type, operating system, and pages visited.
        </li>
      </ul>

      <h2>Use of Information</h2>
      <ul>
        <li>
          <strong>Providing Services:</strong> We use your information to
          provide and improve our services, including facilitating communication
          between healthcare professionals and facilities.
        </li>
        <li>
          <strong>Communications:</strong> We may send you updates, newsletters,
          or promotional offers based on your preferences.
        </li>
        <li>
          <strong>Analytics:</strong> We use analytics tools to analyze trends,
          track user interactions, and improve our services.
        </li>
      </ul>

      <h2>Data Sharing</h2>
      <ul>
        <li>
          <strong>Third-Party Service Providers:</strong> We may share your
          information with trusted third-party service providers who assist us
          in operating our Website and App.
        </li>
        <li>
          <strong>Legal Compliance:</strong> We may disclose information in
          response to legal requests or to protect our rights, property, or
          safety.
        </li>
      </ul>

      <h2>Data Security</h2>
      <ul>
        <li>
          We implement reasonable security measures to protect your information
          from unauthorized access, disclosure, alteration, or destruction.
        </li>
        <li>
          Despite our efforts, no method of transmission over the internet or
          electronic storage is 100% secure. Therefore, we cannot guarantee
          absolute security.
        </li>
      </ul>

      <h2>Your Rights</h2>
      <ul>
        <li>
          <strong>Access and Correction:</strong> You have the right to access
          and correct your personal information held by us.
        </li>
        <li>
          <strong>Data Portability:</strong> You may request to receive your
          information in a structured, commonly used, and machine-readable
          format.
        </li>
        <li>
          <strong>Withdraw Consent:</strong> You can withdraw your consent for
          certain data processing activities at any time.
        </li>
      </ul>

      <h2>Children's Privacy</h2>
      <p>
        Our services are not intended for users under the age of 18. We do not
        knowingly collect personal information from children.
      </p>

      <h2>Changes to Privacy Policy</h2>
      <p>
        We reserve the right to update this Privacy Policy at any time. We will
        notify you of significant changes through our Website or App.
      </p>

      <h2>Contact Us</h2>
      <p>
        If you have any questions, concerns, or requests regarding this Privacy
        Policy or your personal information, please contact us at{' '}
        <a href='mailto:info@locumspace.co'>info@locumspace.co</a>.
      </p>

      <h2>Information about General Data Protection Regulation (GDPR)</h2>
      <p>
        We may be collecting and using information from you if you are from the
        European Economic Area (EEA), and in this section of our Privacy Policy
        we are going to explain exactly how and why this data is collected, and
        how we maintain this data under protection from being replicated or used
        in the wrong way.
      </p>

      <h2>What is GDPR?</h2>
      <p>
        GDPR is an EU-wide privacy and data protection law that regulates how EU
        residents' data is protected by companies and enhances the control the
        EU residents have, over their personal data. The GDPR is relevant to any
        globally operating company and not just the EU-based businesses and EU
        residents. Our customers’ data is important irrespective of where they
        are located, which is why we have implemented GDPR controls as our
        baseline standard for all our operations worldwide.
      </p>

      <h2>What is personal data?</h2>
      <p>
        Any data that relates to an identifiable or identified individual. GDPR
        covers a broad spectrum of information that could be used on its own, or
        in combination with other pieces of information, to identify a person.
        Personal data extends beyond a person’s name or email address. Some
        examples include financial information, political opinions, genetic
        data, biometric data, IP addresses, physical address, sexual
        orientation, and ethnicity.
      </p>

      <h2>The Data Protection Principles include requirements such as:</h2>
      <ul>
        <li>
          Personal data collected must be processed in a fair, legal, and
          transparent way and should only be used in a way that a person would
          reasonably expect.
        </li>
        <li>
          Personal data should only be collected to fulfil a specific purpose
          and it should only be used for that purpose. Organizations must
          specify why they need the personal data when they collect it.
        </li>
        <li>
          Personal data should be held no longer than necessary to fulfil its
          purpose.
        </li>
        <li>
          People covered by the GDPR have the right to access their own personal
          data. They can also request a copy of their data, and that their data
          be updated, deleted, restricted, or moved to another organization.
        </li>
      </ul>

      <h2>Why is GDPR important?</h2>
      <p>
        GDPR adds some new requirements regarding how companies should protect
        individuals' personal data that they collect and process. It also raises
        the stakes for compliance by increasing enforcement and imposing greater
        fines for breach. Beyond these facts, it's simply the right thing to do.
        At Locumspace, we strongly believe that your data privacy is very
        important and we already have solid security and privacy practices in
        place that go beyond the requirements of this new regulation.
      </p>

      <h2>
        Individual Data Subject's Rights - Data Access, Portability and Deletion
      </h2>
      <p>
        We are committed to helping our customers meet the data subject rights
        requirements of GDPR. Locumspace processes or stores all personal data
        in fully vetted, DPA compliant vendors. We do store all conversation and
        personal data for up to 6 years unless your account is deleted. In which
        case, we dispose of all data in accordance with our Terms of Service and
        Privacy Policy, but we will not hold it longer than 60 days.
      </p>
      <p>
        We are aware that if you are working with EU customers, you need to be
        able to provide them with the ability to access, update, retrieve, and
        remove personal data. Our customer support team is here for you to
        answer any questions you might have about working with the API.
      </p>

      <h2>Contact Us</h2>
      <p>
        Don't hesitate to contact us if you have any questions. <br />
        <strong>Via Email:</strong>{' '}
        <a href='mailto:info@locumspace.co'>info@locumspace.co</a>
      </p>
    </Container>
  );
};

const Container = styled.div`
  padding: 1rem;
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  border-radius: 0.375rem;
  color: ${({ theme }) => theme.palette.blackBlack2};
  font-size: 1.125rem;
  display: grid;
  & > h2 {
    margin: 1rem;
  }
  & > p {
    margin: 1rem;
  }

  @media (max-width: 884px) {
    font-size: 14px;
    padding: 8px;
    & > h2 {
      margin: 4px;
    }
    & > p {
      margin: 4px;
    }
    & > ul {
      margin-left: 0px;
      padding-left: 18px;
    }
  }
`;

export default PrivacySettings;
